import React, { useState } from "react";
import { auth } from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";

// Best-effort split of a legacy single "name" into first/last for pre-fill.
const splitName = (name = "") => {
  const parts = name.trim().split(/\s+/);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") || "" };
};

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const seed = splitName(user?.name);
  const [prof, setProf] = useState({
    first_name: user?.first_name ?? seed.first,
    last_name: user?.last_name ?? seed.last,
    display_name: user?.display_name ?? user?.name ?? "",
    email: user?.email ?? "",
    current_password: "",
  });
  const [profMsg, setProfMsg] = useState(null);
  const [profBusy, setProfBusy] = useState(false);
  const emailChanged = (prof.email || "").trim().toLowerCase() !== (user?.email || "").toLowerCase();

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfMsg(null);
    if (emailChanged && !prof.current_password) {
      return setProfMsg({ type: "err", text: "Enter your current password to change your email." });
    }
    setProfBusy(true);
    try {
      const payload = {
        first_name: prof.first_name,
        last_name: prof.last_name,
        display_name: prof.display_name,
        ...(emailChanged ? { email: prof.email, current_password: prof.current_password } : {}),
      };
      const res = await auth.updateProfile(payload);
      await refreshUser();
      setProf((p) => ({ ...p, current_password: "" }));
      setProfMsg({
        type: "ok",
        text: res.email_changed
          ? "Profile saved. A verification link was sent to your new email — confirm it to finish the change."
          : "Profile updated.",
      });
    } catch (e2) {
      setProfMsg({ type: "err", text: e2?.response?.data?.detail || "Could not update profile" });
    } finally {
      setProfBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (pw.next !== pw.confirm) return setMsg({ type: "err", text: "New passwords do not match." });
    setBusy(true);
    try {
      await auth.changePassword(pw.current, pw.next);
      setMsg({ type: "ok", text: "Password updated. Other sessions were signed out." });
      setPw({ current: "", next: "", confirm: "" });
    } catch (e2) {
      setMsg({ type: "err", text: e2?.response?.data?.detail || "Could not change password" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" data-testid="page-settings">
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Account security, API integrations, and preferences.</div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Account</span>
            <span className="pill pill-blue">{user?.role}</span>
          </div>
          <form className="card-form" style={{ padding: 16 }} onSubmit={saveProfile} data-testid="profile-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label>First name</label>
                <input value={prof.first_name} onChange={(e) => setProf({ ...prof, first_name: e.target.value })} data-testid="pf-first" />
              </div>
              <div>
                <label>Last name</label>
                <input value={prof.last_name} onChange={(e) => setProf({ ...prof, last_name: e.target.value })} data-testid="pf-last" />
              </div>
            </div>
            <label>Display name</label>
            <input value={prof.display_name} onChange={(e) => setProf({ ...prof, display_name: e.target.value })} placeholder="How your name appears in the app" data-testid="pf-display" />
            <label>Email <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(also your username)</span></label>
            <input type="email" value={prof.email} onChange={(e) => setProf({ ...prof, email: e.target.value })} autoComplete="email" data-testid="pf-email" />
            {emailChanged && (
              <>
                <div style={{ fontSize: 12, color: "var(--warning)", margin: "4px 0 8px" }}>
                  Changing your email signs you in with the new address and requires re-verification. Enter your current password to confirm.
                </div>
                <label>Current password</label>
                <input type="password" value={prof.current_password} onChange={(e) => setProf({ ...prof, current_password: e.target.value })} autoComplete="current-password" data-testid="pf-current-pw" />
              </>
            )}
            {!user?.email_verified && (
              <div style={{ fontSize: 12, color: "var(--warning)", marginTop: 8 }} data-testid="pf-unverified">⚠ Your email is not verified yet.</div>
            )}
            {profMsg && <div className={profMsg.type === "ok" ? "alert alert-success" : "err"} style={{ marginTop: 10 }} data-testid="pf-msg">{profMsg.text}</div>}
            <button className="btn btn-primary btn-sm" type="submit" style={{ marginTop: 14 }} disabled={profBusy} data-testid="pf-submit">
              {profBusy ? <span className="spinner white" /> : "Save changes"}
            </button>
          </form>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Change Password</span></div>
          <form className="card-form" style={{ padding: 16 }} onSubmit={changePw}>
            <label>Current password</label>
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required autoComplete="current-password" data-testid="cp-current" />
            <label>New password</label>
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required autoComplete="new-password" data-testid="cp-next" />
            <label>Confirm new password</label>
            <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required autoComplete="new-password" data-testid="cp-confirm" />
            {msg && <div className={msg.type === "ok" ? "alert alert-success" : "err"} style={{ marginTop: 10 }} data-testid="cp-msg">{msg.text}</div>}
            <button className="btn btn-primary btn-sm" type="submit" style={{ marginTop: 14 }} disabled={busy} data-testid="cp-submit">
              {busy ? <span className="spinner white" /> : "Update password"}
            </button>
          </form>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header"><span className="card-title">AI Configuration</span></div>
          <div style={{ padding: 16 }}>
            <div className="flex justify-between" style={{ fontSize: 13, padding: "8px 0" }}>
              <span>LLM Provider</span><span className="font-600">Anthropic</span>
            </div>
            <div className="flex justify-between" style={{ fontSize: 13, padding: "8px 0", borderTop: "1px solid var(--border)" }}>
              <span>Model</span><span className="font-600">Claude Sonnet 4.5</span>
            </div>
            <div className="flex justify-between" style={{ fontSize: 13, padding: "8px 0", borderTop: "1px solid var(--border)" }}>
              <span>Embedding Strategy</span><span className="font-600">Lexical RAG (MVP)</span>
            </div>
            <div className="flex justify-between" style={{ fontSize: 13, padding: "8px 0", borderTop: "1px solid var(--border)" }}>
              <span>Status</span><span className="pill pill-green">Operational</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Integrations</span></div>
          <div style={{ padding: 16 }}>
            {["SAM.gov sync", "GovWin IQ", "Bloomberg Government", "Microsoft 365 / SharePoint", "Slack notifications"].map((s, i) => (
              <div key={i} className="flex justify-between" style={{ fontSize: 13, padding: "10px 0", borderTop: i ? "1px solid var(--border)" : "none" }}>
                <span>{s}</span><span className="pill pill-gray">Coming Soon</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Compliance &amp; Security</span></div>
        <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, fontSize: 13 }}>
          <div><strong>Data residency:</strong> US-only</div>
          <div><strong>Encryption:</strong> AES-256 at rest</div>
          <div><strong>FedRAMP:</strong> Moderate (roadmap)</div>
          <div><strong>Audit log:</strong> Enabled</div>
          <div><strong>RBAC:</strong> Enabled</div>
          <div><strong>SSO:</strong> Coming Soon</div>
        </div>
      </div>
    </div>
  );
}
